import { useAppStore } from '../../store/useAppStore';
import { useAnimation } from '../../hooks/animation/useAnimation';
import { useResize } from '../../hooks/viewport/useResize';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { MediaGallery } from './MediaGallery';
import { videoCache } from '../../hooks/media/useMediaLoader';
import { textureCache } from '../../hooks/media/useMediaLoader';
import { Link } from '../common/Link';

const ANIMATION_CONFIG = {
  duration: 300,
  easing: (t: number) => t * (2 - t),
};

const GALLERY_SPACING = {
  MOBILE: 20,
  DESKTOP: 70,
} as const;

export function ProjectDetails() {
  const selectedProject = useAppStore(state => state.selectedProject);
  const setSelected = useAppStore(state => state.setSelected);
  const isTransitionComplete = useAppStore(state => state.isTransitionComplete);
  const setGalleryScrollOffset = useAppStore(
    state => state.setGalleryScrollOffset
  );

  const { height } = useResize();
  const animate = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasAnimatedRef = useRef(false);

  const [containerHeight, setContainerHeight] = useState<number>(height);
  const [isAnimating, setIsAnimating] = useState(false);

  // Initialisation immédiate
  useEffect(() => {
    const initContainer = () => {
      if (containerRef.current?.parentElement) {
        const parentHeight = containerRef.current.parentElement.clientHeight;
        setContainerHeight(parentHeight);
      }
    };

    initContainer();

    // Réessayer plusieurs fois au cas où le DOM n'est pas prêt
    const timeouts = [
      setTimeout(initContainer, 0),
      setTimeout(initContainer, 100),
    ];

    const observer = new ResizeObserver(initContainer);

    if (containerRef.current?.parentElement) {
      observer.observe(containerRef.current.parentElement);
    }

    return () => {
      timeouts.forEach(clearTimeout);
      observer.disconnect();
    };
  }, [selectedProject, isTransitionComplete]);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (scrollRef.current) {
        const delta = e.deltaY;
        const isMobile = window.innerWidth < 768;
        const galleryContainer = scrollRef.current;

        if (isMobile) {
          galleryContainer.scrollTop += delta;
          setGalleryScrollOffset(galleryContainer.scrollTop);
        } else {
          galleryContainer.scrollLeft += delta;
          setGalleryScrollOffset(galleryContainer.scrollLeft);
        }
      }
    },
    [setGalleryScrollOffset]
  );

  // Gestion du scroll
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !selectedProject || !isTransitionComplete) return;

    // Réinitialisation du scroll
    element.scrollLeft = 0;

    // Nettoyage de l'ancien listener
    element.removeEventListener('wheel', handleWheel);
    // Ajout du nouveau listener
    element.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      if (element) {
        element.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel, selectedProject, isTransitionComplete]);

  const INITIAL_STYLES = {
    willChange: 'opacity',
    opacity: '0',
  } as const;

  const handleAnimation = useCallback(
    (element: HTMLElement, isClosing: boolean) => {
      if (isAnimating) return;
      if (!isClosing && hasAnimatedRef.current) return;

      setIsAnimating(true);

      requestAnimationFrame(() => {
        animate(
          progress => {
            const opacity = isClosing ? 1 - progress : progress;
            if (element) {
              element.style.opacity = String(opacity);
            }
          },
          {
            ...ANIMATION_CONFIG,
            onComplete: () => {
              setIsAnimating(false);
              if (isClosing) {
                requestAnimationFrame(() => {
                  setSelected(null, null);
                });
              } else {
                hasAnimatedRef.current = true;
              }
            },
          }
        );
      });
    },
    [animate, setSelected]
  );

  const handleClose = useCallback(() => {
    const element = document.getElementById('project-details');
    if (!element) return;

    handleAnimation(element, true);
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject) {
      setGalleryScrollOffset(0);
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
        scrollRef.current.scrollLeft = 0;
      }
    }
  }, [selectedProject, setGalleryScrollOffset]);

  useEffect(() => {
    let mounted = true;

    if (!selectedProject) {
      hasAnimatedRef.current = false;
      return;
    }

    if (isTransitionComplete && !hasAnimatedRef.current && mounted) {
      const element = document.getElementById('project-details');
      if (element) {
        requestAnimationFrame(() => {
          if (mounted) {
            handleAnimation(element, false);
          }
        });
      }
    }

    return () => {
      mounted = false;
    };
  }, [selectedProject, isTransitionComplete]);

  const { totalWidth, totalHeight } = useMemo(() => {
    if (!selectedProject?.projectMedia?.length)
      return { totalWidth: 0, totalHeight: 0 };

    const isMobile = window.innerWidth < 768;
    const verticalPadding = 96;
    const availableHeight = containerHeight - verticalPadding;
    const mobileScreenWidth = window.innerWidth;

    const dimensions = selectedProject.projectMedia.reduce(
      (acc, media, index, array) => {
        const videoWidth = videoCache.get(media)?.videoWidth;
        const videoHeight = videoCache.get(media)?.videoHeight;
        const imageWidth = textureCache.get(media)?.image.width;
        const imageHeight = textureCache.get(media)?.image.height;

        const aspectRatio =
          media.endsWith('.mp4') || media.endsWith('.webm')
            ? videoWidth !== undefined && videoHeight !== undefined
              ? videoWidth / videoHeight
              : 16 / 9
            : imageWidth !== undefined && imageHeight !== undefined
              ? imageWidth / imageHeight
              : 16 / 9;

        const gap =
          index < array.length - 1
            ? isMobile
              ? GALLERY_SPACING.MOBILE
              : GALLERY_SPACING.DESKTOP
            : 0;

        if (isMobile) {
          const mediaHeight = mobileScreenWidth / aspectRatio;
          return {
            width: mobileScreenWidth,
            height: acc.height + mediaHeight + gap,
          };
        } else {
          const mediaWidth = availableHeight * aspectRatio * 0.5;
          return {
            width: acc.width + mediaWidth + gap,
            height: availableHeight,
          };
        }
      },
      { width: 0, height: 0 }
    );

    return {
      totalWidth: dimensions.width,
      totalHeight: dimensions.height,
    };
  }, [selectedProject?.projectMedia, containerHeight]);

  if (!selectedProject || !isTransitionComplete) return null;

  return (
    <>
      <button
        onClick={handleClose}
        className="fixed md:top-10 inset-x-0 mx-auto w-fit z-[60] p-4 group opacity-0 animate-fade-in text-black"
        style={{ animationDelay: '1500ms' }}
        aria-label="Close details"
      >
        <svg
          className="w-8 h-8 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:rotate-90"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      <div
        id="project-details"
        className="fixed bottom-0 left-0 right-0 z-10 h-screen pointer-events-none"
        style={INITIAL_STYLES}
      >
        <div className="relative h-full pointer-events-auto">
          <div className="pt-14 h-full pointer-events-auto">
            <div
              className="flex h-full overflow-y-auto md:overflow-y-hidden md:overflow-x-auto overflow-x-hidden scrollbar-hide p-8 pb-0 md:pb-8"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              ref={scrollRef}
            >
              <div className="flex flex-col md:flex-row gap-12 text-xs lg:text-sm items-start py-12">
                <div className="flex flex-col h-auto w-full mt-[150px] md:w-[70vw] pt-12">
                  <h1
                    className="text-2xl font-bold mb-2 opacity-0 animate-fade-in text-black"
                    style={{ animationDelay: '300ms' }}
                  >
                    {selectedProject.title}
                  </h1>
                  <p
                    className="mb-2 opacity-0 animate-fade-in"
                    style={{ animationDelay: '400ms' }}
                  >
                    <strong className="text-black">Year:</strong>{' '}
                    {selectedProject.year}
                  </p>
                  <p
                    className="mb-2 opacity-0 animate-fade-in"
                    style={{ animationDelay: '500ms' }}
                  >
                    <strong className="text-black">Context:</strong>{' '}
                    {selectedProject.context}
                  </p>
                  {selectedProject?.links &&
                    selectedProject?.links?.length > 0 && (
                      <div
                        className="mb-2 opacity-0 animate-fade-in"
                        style={{ animationDelay: '600ms' }}
                      >
                        <>
                          <strong className="text-black">Links:</strong>
                          {selectedProject.links.map(link =>
                            link.url && link.url !== 'N/A' ? (
                              <Link
                                key={link.url}
                                href={link.url}
                                isExternal
                                className="ml-2"
                              >
                                {link.label}
                              </Link>
                            ) : (
                              <span
                                key={link.label}
                                className="ml-2 cursor-not-allowed"
                              >
                                {link.label}
                              </span>
                            )
                          )}
                        </>
                      </div>
                    )}
                  <div
                    className="flex gap-2 mt-2 flex-wrap opacity-0 animate-fade-in"
                    style={{ animationDelay: '700ms' }}
                  >
                    {selectedProject.techStack.map(tech => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-black text-white rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <p
                    className="mt-4 opacity-0 animate-fade-in"
                    style={{ animationDelay: '800ms' }}
                    dangerouslySetInnerHTML={{
                      __html: selectedProject.description,
                    }}
                  />
                </div>
                {selectedProject?.projectMedia &&
                  Array.isArray(selectedProject.projectMedia) &&
                  selectedProject.projectMedia.length > 0 && (
                    <div
                      className="h-full w-full md:w-auto opacity-0 animate-fade-in"
                      style={{ animationDelay: '1200ms' }}
                      ref={containerRef}
                    >
                      <div
                        className="h-full mb-6 md:mb-0"
                        style={{
                          width:
                            window.innerWidth < 768
                              ? '100%'
                              : `${totalWidth}px`,
                          height:
                            window.innerWidth < 768
                              ? `${totalHeight}px`
                              : '100%',
                        }}
                      >
                        <Canvas>
                          <MediaGallery media={selectedProject.projectMedia} />
                        </Canvas>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
